import type { SupportedLang } from "./i18nContext";

const translations: Record<SupportedLang, Record<string, string>> = {
  en: {
    // ─── NAV ───
    "nav.howItWorks": "How it works",
    "nav.preview": "Preview",
    "nav.pricing": "Pricing",
    "nav.goToApp": "Go to app",
    "nav.getStarted": "Get started",
    "nav.signIn": "Sign in",
    "nav.back": "Back",
    "nav.dashboard": "Dashboard",

    // ─── HERO ───
    "hero.title": "Open your restaurant, café, or bar — with a clear plan.",
    "hero.subtitle": "The complete toolkit for food & beverage entrepreneurs — from first lease to full operations.",
    "hero.cta": "Start your free plan",
    "hero.cta2": "See how it works",
    "hero.trusted": "Trusted by first-time founders and experienced operators",
    "hero.stopGuessing": "Open smarter.",
    "hero.startOpening": "Run better.",
    "hero.createPlan": "Create your opening plan",
    "hero.seeLiveExample": "See live example",
    "hero.freeToStart": "Free to start · No credit card required",
    "hero.saveResearch": "Save 3–6 months of research",
    "hero.badge": "For restaurants, hotels, cafés & hospitality concepts",

    // ─── HOW IT WORKS ───
    "how.title": "How it works",
    "how.subtitle": "Three steps. One plan. Zero experience required.",
    "how.step1.title": "Tell us what you're opening",
    "how.step1.desc": "Choose your city, business type, square footage, budget, and opening target.",
    "how.step2.title": "Get your AI opening roadmap",
    "how.step2.desc": "We generate your permits, budget, timeline, location suggestions, and key risks instantly.",
    "how.step3.title": "Open with confidence",
    "how.step3.desc": "Use your roadmap, export your plan, and ask the AI assistant questions along the way.",

    // ─── FEATURES ───
    "features.title": "Everything you need to open",
    "features.subtitle": "One platform replaces weeks of research, expensive consultants, and scattered spreadsheets.",
    "features.location.title": "Find the right location",
    "features.location.desc": "See real leasing insights, neighborhood fit, rent expectations, and zoning risks before you sign.",
    "features.permits.title": "Get every permit handled",
    "features.permits.desc": "Know exactly which permits and licenses you need, how much they cost, how long they take, and where to apply.",
    "features.budget.title": "Build a realistic budget",
    "features.budget.desc": "Estimate startup costs, monthly burn, and break-even range before committing to a space.",
    "features.timeline.title": "Follow a week-by-week timeline",
    "features.timeline.desc": "Get a clear roadmap from entity setup to inspections, hiring, and opening day.",
    "features.mistakes.title": "Avoid expensive mistakes",
    "features.mistakes.desc": "Spot common issues early, from liquor license delays to grease trap requirements and zoning conflicts.",
    "features.export.title": "Export and share your plan",
    "features.export.desc": "Download a polished PDF to use with partners, landlords, banks, or investors.",

    // ─── ROADMAP ───
    "roadmap.title": "Your AI Opening Roadmap",
    "roadmap.subtitle": "Your complete opening plan, generated in seconds.",
    "roadmap.desc": "Answer a few simple questions and Oplif™ generates a personalized roadmap for your business — covering permits, costs, timelines, lease guidance, and common mistakes to avoid.",
    "roadmap.bullet1": "Every permit mapped",
    "roadmap.bullet2": "Realistic budget",
    "roadmap.bullet3": "Week-by-week timeline",
    "roadmap.bullet4": "Location recommendations",
    "roadmap.bullet5": "Risk alerts before you sign",
    "roadmap.bullet6": "Exportable investor-ready PDF",

    // ─── FOUNDING MEMBER ───
    "founding.spotsRemaining": "{count} of 50 spots remaining",
    "founding.limited": "Limited to 50 spots",
    "founding.title": "Be one of our first 50 founding members",
    "founding.desc": "Get free Pro access in exchange for honest feedback. Help us build the best opening platform for restaurant founders.",
    "founding.joining": "Joining...",
    "founding.join": "Join as Founding Member",
    "founding.alreadySignedUp": "You've already signed up!",
    "founding.error": "Something went wrong. Please try again.",
    "founding.success": "You're in! Check your email shortly ✓",

    // ─── PRICING ───
    "pricing.title": "Simple, transparent pricing",
    "pricing.subtitle": "Start free. Upgrade when you're ready to go all in.",
    "pricing.monthly": "Monthly",
    "pricing.annual": "Annual",
    "pricing.save20": "Save 20%",
    "pricing.custom": "Custom",
    "pricing.mo": "/mo",
    "pricing.noCreditCard": "No credit card required",
    "pricing.free.name": "Free",
    "pricing.free.micro": "Explore the platform. See what's possible.",
    "pricing.free.cta": "Start free",
    "pricing.free.f1": "1 roadmap",
    "pricing.free.f2": "Basic permit list",
    "pricing.free.f3": "Basic timeline",
    "pricing.free.f4": "5 AI questions",
    "pricing.free.x1": "No PDF export",
    "pricing.free.x2": "No location map",
    "pricing.free.x3": "No forecast",
    "pricing.free.x4": "No post-opening mode",
    "pricing.pro.name": "Pro",
    "pricing.pro.micro": "Everything you need to open and operate.",
    "pricing.pro.cta": "Start growing",
    "pricing.pro.badge": "Most Popular",
    "pricing.pro.f1": "10 roadmaps",
    "pricing.pro.f2": "Everything in Free, plus more",
    "pricing.pro.f3": "Location map",
    "pricing.pro.f4": "Unlimited AI assistant",
    "pricing.pro.f5": "Post-opening operations",
    "pricing.pro.f6": "Health inspection prep",
    "pricing.pro.f7": "Lease tracker",
    "pricing.pro.f8": "Permit renewal reminders",
    "pricing.pro.f9": "Survival score",
    "pricing.pro.f10": "Priority support",
    "pricing.studio.name": "Studio",
    "pricing.studio.micro": "For teams scaling multiple concepts.",
    "pricing.studio.cta": "Scale up",
    "pricing.studio.f1": "25 roadmaps",
    "pricing.studio.f2": "Everything in Pro",
    "pricing.studio.f3": "5 team members",
    "pricing.studio.f4": "White-label PDF",
    "pricing.studio.f5": "Analytics dashboard",
    "pricing.studio.f6": "Dedicated account manager",
    "pricing.enterprise.name": "Enterprise",
    "pricing.enterprise.micro": "For multi-unit groups and large teams.",
    "pricing.enterprise.cta": "Contact us",
    "pricing.enterprise.f1": "Unlimited everything",
    "pricing.enterprise.f2": "SSO",
    "pricing.enterprise.f3": "API access",
    "pricing.enterprise.f4": "Custom integrations",
    "pricing.enterprise.f5": "Dedicated onboarding",

    // ─── FINAL CTA ───
    "cta.title": "Open smarter. Spend less. Avoid costly mistakes.",
    "cta.subtitle": "Get your city-specific opening roadmap with permits, budget, timeline, and location guidance in minutes.",
    "cta.button": "Get my opening plan",
    "cta.free": "Free to start · No credit card required",

    // ─── FOOTER ───
    "footer.tagline": "The complete toolkit for food & beverage entrepreneurs — from first lease to full operations.",
    "footer.product": "Product",
    "footer.company": "Company",
    "footer.legal": "Legal",
    "footer.about": "About",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.copyright": "© 2026 Oplif™. All rights reserved.",

    // ─── PDF ───
    "pdf.download": "Download PDF",
    "pdf.preview": "Preview Plan",
    "pdf.downloaded": "PDF downloaded!",
    "pdf.error": "Failed to generate PDF. Please try again.",

    // ─── DASHBOARD ───
    "dash.title": "Your {business} in {city}",
    "dash.subtitle": "Here's your personalized opening roadmap.",
    "dash.viewPlan": "View & Download Plan",
    "dash.quickDownload": "Quick Download",
    "dash.openingSummary": "Opening Summary",
    "dash.beginnerMode": "Beginner mode",
    "dash.askAI": "Ask AI",
    "dash.timeline": "Timeline",
    "dash.budget": "Budget",
    "dash.openingScore": "Opening Score",
    "dash.budgetFit": "Budget Fit",
    "dash.weekByWeek": "Week-by-Week Timeline",
    "dash.estimatedStaffing": "Estimated Staffing",
    "dash.totalMonthlyLabor": "Total monthly labor",
    "dash.spaceGuidance": "Space Guidance",
    "dash.recommendedSqft": "Recommended square footage",
    "dash.startupCost": "Startup Cost Breakdown",
    "dash.total": "Total",
    "dash.milestones": "Milestones Before Opening Day",
    "dash.openingDay": "Opening day",
    "dash.before": "before",
    "dash.commonMistakes": "Common Mistakes to Avoid",
    "dash.first7Days": "Your First 7 Days",
    "dash.locations": "Location Recommendations",
    "dash.openingTimeline": "Opening Timeline",
    "dash.permits": "Permits & Licenses",
    "dash.budgetPlanner": "Budget Planner",
    "dash.forecast": "Financial Forecast",
    "dash.checklist": "Opening Checklist",
    "dash.assistant": "AI Assistant",
    "dash.shareRoadmap": "Share Roadmap",
    "dash.shareCopied": "Share link copied — send it to your partner or investor.",
    "dash.tabOpening": "Opening Plan",
    "dash.tabOperations": "Operations",
    "dash.tabLocations": "Locations",
    "dash.readinessScore": "Your Readiness Score",
    "dash.readinessTooltip": "This score estimates how prepared you are to open based on your budget, location, and plan completeness. 80+ is great, below 60 means you may have gaps to address.",
    "dash.outOf100": "out of 100",
    "dash.greatShape": "You're in great shape!",
    "dash.fewThings": "A few things to work on",
    "dash.needsPrep": "Needs more preparation",
    "dash.strongPosition": "Strong Position",
    "dash.needsAttention": "Needs Attention",
    "dash.highRisk": "High Risk",
    "dash.startupInvestment": "Startup Investment",
    "dash.startupTooltip": "This is the total estimated cost to open your doors — including build-out, equipment, permits, and initial inventory.",
    "dash.howMuchStart": "How much you'll need to start",
    "dash.yourBudget": "Your budget: {label}",
    "dash.budgetLabel": "Budget: {label} · Fit: ",
    "dash.monthlyLabor": "Monthly Labor",
    "dash.monthlyStaffCosts": "Monthly staff costs",
    "dash.laborTooltip": "This is what you'll spend each month on employee wages. It's usually your biggest ongoing cost — try to keep it under 30% of revenue.",
    "dash.essentialRoles": "{count} must-have roles",
    "dash.essentialRolesDetail": "{count} essential roles · {team}",
    "dash.timeToOpen": "Time to open",
    "dash.permitsNeeded": "Permits needed",
    "dash.rolesToHire": "Roles to hire",
    "dash.todoItems": "To-do items",
    "dash.requiredPermits": "{count} Required Permits",
    "dash.checklistItems": "{count} checklist items",
    "dash.riskFlags": "{count} Risk Flags",
    "dash.founderMistakes": "Common founder mistakes to avoid",
    "dash.setupDetails": "Your Setup Details",
    "dash.setupTooltip": "These are the details you entered during onboarding. They drive all the estimates and recommendations on this page.",
    "dash.whoToHire": "Who you'll need to hire",
    "dash.staffingTooltip": "These are the roles you'll likely need. 'Essential' roles are must-haves from day one. 'Recommended' roles help as you grow.",
    "dash.optionalHidden": "+ {count} optional roles hidden · turn off Beginner Mode to see all",
    "dash.totalWages": "Total monthly wages",
    "dash.howMuchSpace": "How much space you need",
    "dash.spaceTooltip": "Based on your business type and seating needs, this is the range of square footage that works best. Too small and you'll be cramped; too big and rent eats your profits.",
    "dash.recommendedSize": "Recommended size",
    "dash.seatingCapacity": "Seating Capacity",
    "dash.indoor": "Indoor",
    "dash.outdoor": "Outdoor",
    "dash.keyDeadlines": "Key deadlines",
    "dash.milestonesTooltip": "These are the big deadlines you need to hit before opening day. The ones with a green dot are the most critical — don't miss them.",
    "dash.budgetReality": "Budget Reality Check",
    "dash.budgetTight": "Based on market data, your budget of {label} may be tight — the realistic minimum is {min}. Here's what founders typically underestimate:",
    "dash.permitFees": "Permit & license fees",
    "dash.preOpenPayroll": "Pre-opening payroll",
    "dash.contingencyBuffer": "Contingency buffer (18%)",
    "dash.lockedTimeline": "Full Timeline — Pro plan",
    "dash.lockedTimelineDesc": "Upgrade to see every phase of your opening mapped out week by week.",
    "dash.lockedStaffing": "Staffing & Space Analysis — Pro plan",
    "dash.lockedStaffingDesc": "Get role-by-role hiring recommendations and space layout guidance tailored to your concept.",
    "dash.lockedMilestones": "Opening Milestones — Pro plan",
    "dash.lockedMilestonesDesc": "See every critical milestone mapped to your opening date so nothing falls through the cracks.",
    "dash.days": "days",

    // ─── LOCATIONS ───
    "loc.title": "Location Recommendations",
    "loc.available": "Available to lease",
    "loc.leased": "Leased",
    "loc.searchLeases": "Search live lease listings for",
    "loc.linksOpen": "Links open real-time commercial listings filtered for",
    "loc.neighborhoods": "Neighborhood Analysis",
    "loc.rent": "Rent",
    "loc.traffic": "Traffic",
    "loc.beginnerOK": "Beginner OK",
    "loc.fitScore": "Fit Score",
    "loc.beginnerTip": "This area is recommended for first-time business owners because of the manageable rent and supportive local community.",
    "loc.neighborhoodGuide": "{city} Neighborhood Guide",
    "loc.bestAreas": "Best areas for your {business} — sorted by match",
    "loc.neighborhoodAnalysis": "Neighborhood analysis for {city}",
    "loc.liveListings": "📍 Live listings coming soon — use this guide to plan your location strategy in {city}.",
    "loc.cityNote": "⚠️ {city} Note",
    "loc.bestMatch": "Best Match",
    "loc.lowestRent": "Lowest Rent",
    "loc.highestTraffic": "Highest Traffic",
    "loc.upAndComing": "Up & Coming",
    "loc.map": "Map",
    "loc.cards": "Cards",
    "loc.matchingFor": "Matching for:",
    "loc.greatMatch": "Great match (85%+)",
    "loc.goodMatch": "Good match (70–84%)",
    "loc.possible": "Possible (<70%)",
    "loc.avgSpend": "Avg Spend",
    "loc.competition": "Competition",
    "loc.insiderTip": "Insider tip:",
    "loc.comingSoon": "Neighborhood analysis coming soon for {city}",
    "loc.comingSoonDesc": "We're researching neighborhoods, rents, foot traffic, and competition data for {city}. You'll get match scores tailored to your {business} concept.",
    "loc.notifyMe": "Notify me when it launches",
    "loc.backToDashboard": "Back to Dashboard",
    "loc.inTheMeantime": "💡 In the meantime, use the {link} to ask about neighborhoods in {city} — it knows the city well.",
    "loc.aiAssistant": "AI Assistant",

    // ─── TIMELINE ───
    "timeline.title": "Opening Timeline",
    "timeline.estimated": "Estimated: {time}",
    "timeline.liquorWarning": "Important — Submit your liquor license application in Week 1",
    "timeline.liquorDetail": "Do not wait until your space is ready. The Florida SLA liquor license process takes 3–6 months minimum and is the #1 reason restaurants open late in Miami.",
    "timeline.taskCalendar": "Task Calendar",
    "timeline.assign": "Assign",
    "timeline.notes": "Notes",
    "timeline.addNote": "Add note",
    "timeline.save": "Save",
    "timeline.cancel": "Cancel",
    "timeline.addNotePlaceholder": "Add a note…",

    // ─── PERMITS ───
    "permits.title": "Permits & Licenses",
    "permits.verified": "{count} verified permits for your city",
    "permits.whatYouNeed": "What you may need — with details",
    "permits.realData": "Real government data",
    "permits.costsVerified": "Costs verified from official .gov sources · Last updated: {date}",
    "permits.required": "Required",
    "permits.optional": "Optional",
    "permits.total": "Total",
    "permits.searchPermits": "Search permits...",
    "permits.expandAll": "Expand All",
    "permits.collapse": "Collapse",
    "permits.all": "All",
    "permits.processingTime": "Processing Time",
    "permits.estimatedCost": "Estimated Cost",
    "permits.whatYouNeedDocs": "What You Need",
    "permits.applyNow": "Apply Now — Official Site",
    "permits.brokenLink": "Report broken link",
    "permits.brokenLinkReported": "Broken link reported",
    "permits.brokenLinkDesc": "We'll check the link for \"{name}\" and update it.",
    "permits.ifBroken": "If this link doesn't work, search \"{name} {city}\" on Google.",
    "permits.govWarning": "Government websites occasionally change URLs. If this link doesn't work, search \"{name} {city}\" on Google.",
    "permits.infoRequired": "These are the exact permits required in {city}. Tap any permit for costs, timelines, and direct application links.",
    "permits.infoGeneric": "Requirements depend on city, county, state, and business model. Always verify with local authorities.",
    "permits.lockedCount": "{count} more permits locked",
    "permits.upgradeToSee": "Upgrade to Opener to see all permits",
    "permits.upgrade": "Upgrade",
    "permits.mayBeNeeded": "May Be Needed ({count})",
    "permits.noMatch": "No permits match your search.",

    // ─── BUDGET ───
    "budget.title": "Budget Planner",
    "budget.itemized": "Itemized startup costs for a {business}",
    "budget.advisory": "Budget Advisory",
    "budget.miamiWarning": "Heads up — opening a restaurant in Miami typically requires at least {amount} in startup capital. Consider adjusting your budget before proceeding.",
    "budget.strong": "Strong Budget",
    "budget.balanced": "Balanced Budget",
    "budget.underfunded": "Underfunded Budget",
    "budget.strongMsg": "Your budget looks strong for this type of business.",
    "budget.balancedMsg": "Your budget is workable but leave room for surprises.",
    "budget.underfundedMsg": "Your budget may be tight. Consider a smaller format or lower-cost area.",
    "budget.estimatedTotal": "Estimated Total Range",
    "budget.basedOn": "Based on typical costs for a {business} in {city}. Adjusted for local rent, labor, and construction costs.",
    "budget.disclaimer": "⚠️ Disclaimer: These are estimated ranges adjusted to the {city} market using local rent, labor, and construction cost indices. Actual costs vary by exact location, condition of space, equipment quality (new vs used), and vendor. Always get 3+ quotes before committing.",
    "budget.alcoholGuidance": "Alcohol License Guidance",
    "budget.compareOptions": "Compare your options:",
    "budget.licenseType": "License Type",
    "budget.cost": "Cost",
    "budget.timelineLabel": "Timeline",
    "budget.details": "Details",

    // ─── CHECKLIST ───
    "checklist.title": "Opening Plan",
    "checklist.stepsComplete": "{done} of {total} steps complete",
    "checklist.complete": "complete",
    "checklist.foundation": "Foundation",
    "checklist.halfway": "Halfway",
    "checklist.almostThere": "Almost There",
    "checklist.readyToOpen": "Ready to Open!",
    "checklist.done": "Done",
    "checklist.remaining": "Remaining",
    "checklist.daysToOpening": "~{days} days to opening",
    "checklist.readyEmoji": "🎉 Ready to open!",
    "checklist.whereToGet": "Where to get it:",

    // ─── ASSISTANT ───
    "assistant.title": "AI Business Assistant",
    "assistant.helping": "Helping you open your {business}",
    "assistant.askAnything": "Ask anything about opening your business",
    "assistant.queries": "{used} of {limit} queries",
    "assistant.placeholder": "Ask about permits, locations, budget, staffing...",
    "assistant.signIn": "Please sign in to use the assistant.",
    "assistant.noResponse": "No response received.",
    "assistant.connectionError": "Connection error. Please check your internet and try again.",
    "assistant.limitReached": "You've used all {limit} AI queries this month.",
    "assistant.upgradeMore": "Upgrade for more →",

    // ─── FORECAST ───
    "forecast.title": "Financial Forecast",
    "forecast.subtitle": "Estimate your revenue, costs, and break-even — before you commit to anything.",
    "forecast.disclaimer": "This is an estimated planning tool, not guaranteed financial advice. Actual results will vary based on location, execution, and market conditions.",
    "forecast.assumptions": "Your Assumptions",
    "forecast.avgTicket": "Average Ticket ({cs})",
    "forecast.customersPerDay": "Customers Per Day",
    "forecast.monthlyRent": "Monthly Rent ({cs})",
    "forecast.cogs": "COGS (%)",
    "forecast.laborPercent": "Labor Cost (%)",
    "forecast.otherExpenses": "Other Monthly Expenses ({cs})",
    "forecast.startupBudget": "Startup budget:",
    "forecast.daysWeek": "{days} days/week",
    "forecast.conservative": "Conservative",
    "forecast.realistic": "Realistic",
    "forecast.optimistic": "Optimistic",
    "forecast.monthlyRevenue": "Monthly Revenue",
    "forecast.monthlyProfit": "Monthly Profit",
    "forecast.cashFlowPositive": "Cash Flow Positive",
    "forecast.fullROI": "Full ROI Timeline",
    "forecast.atFullCapacity": "at full capacity",
    "forecast.profitable": "profitable",
    "forecast.operatingLoss": "operating at a loss",
    "forecast.revenueCovers": "revenue covers monthly expenses",
    "forecast.revenueVsExpenses": "Revenue vs Expenses (12 months)",
    "forecast.profitComparison": "Profit Comparison — All Scenarios",
    "forecast.monthlyBreakdown": "Monthly Breakdown — {scenario}",
    "forecast.month": "Month",
    "forecast.revenue": "Revenue",
    "forecast.expenses": "Expenses",
    "forecast.profit": "Profit",
    "forecast.cumCash": "Cum. Cash",
    "forecast.labor": "Labor",
    "forecast.other": "Other",
    "forecast.scenarioSummary": "Scenario Summary",
    "forecast.annualRevenue": "Annual revenue",
    "forecast.annualProfit": "Annual profit",
    "forecast.fullRoi": "Full ROI",
    "forecast.assumptionsMethod": "Assumptions & Methodology",

    // ─── EQUIPMENT ───
    "equipment.title": "Equipment & Inventory",
    "equipment.subtitle": "Everything you need before opening day",
    "equipment.progress": "Progress",
    "equipment.acquired": "{count} of {total} acquired",
    "equipment.acquiredCost": "Acquired: ~{amount}",
    "equipment.estTotal": "Est. Total: ~{amount}",
    "equipment.allCategories": "All Categories",
    "equipment.allPriorities": "All Priorities",
    "equipment.addItem": "Add Item",
    "equipment.addCustomItem": "Add Custom Item",
    "equipment.emoji": "Emoji",
    "equipment.name": "Name *",
    "equipment.minPrice": "Min Price ({cs})",
    "equipment.maxPrice": "Max Price ({cs})",
    "equipment.category": "Category",
    "equipment.priority": "Priority",
    "equipment.proTip": "Pro Tip",
    "equipment.whereToBuy": "Where to Buy",
    "equipment.itemRequired": "Item name is required",
    "equipment.itemAdded": "Item added!",

    // ─── ONBOARDING ───
    "onboarding.step": "Step {current} of {total}",
    "onboarding.welcome": "Welcome to oplif.™",
    "onboarding.welcomeDesc": "The smarter way to plan, open, and run your food & beverage business.",
    "onboarding.personalize": "We'll personalize your experience in under 60 seconds.",
    "onboarding.tellUs": "Tell us about your business",
    "onboarding.tellUsDesc": "This helps us tailor your roadmap, permits, and cost estimates to your specific market.",
    "onboarding.businessName": "Business name",
    "onboarding.businessNamePlaceholder": "e.g. The Golden Plate",
    "onboarding.businessType": "Business type",
    "onboarding.country": "Country",
    "onboarding.selectCountry": "Select a country",
    "onboarding.city": "City",
    "onboarding.goal": "What's your goal?",
    "onboarding.goalDesc": "We'll customize your dashboard based on where you are in your journey.",
    "onboarding.goalOpening": "Opening a new business",
    "onboarding.goalOpeningDesc": "I'm planning to launch and need a step-by-step roadmap to get there.",
    "onboarding.goalManaging": "Managing an existing one",
    "onboarding.goalManagingDesc": "Run a sharper operation with real-time labor, food, and rent tracking, automated permit and lease renewal alerts, monthly survival scoring, and benchmarks against comparable venues in your market.",
    "onboarding.choosePlan": "Choose your plan",
    "onboarding.choosePlanDesc": "Start free, upgrade when you're ready.",
    "onboarding.letsGo": "Let's go",
    "onboarding.continue": "Continue",
    "onboarding.launchDashboard": "Launch my dashboard",
    "onboarding.popular": "Popular",
    "onboarding.comingSoon": "Coming soon",

    // ─── AUTH ───
    "auth.welcomeBack": "Welcome back",
    "auth.createAccount": "Create your account",
    "auth.signInDesc": "Sign in to pick up where you left off.",
    "auth.signUpDesc": "Join thousands of founders planning smarter openings.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.signIn": "Sign in",
    "auth.signUp": "Create account",
    "auth.or": "or",
    "auth.continueGoogle": "Continue with Google",
    "auth.noAccount": "Don't have an account?",
    "auth.hasAccount": "Already have an account?",
    "auth.signUpLink": "Sign up",
    "auth.signInLink": "Sign in",
    "auth.accountCreated": "Account created — check your email to verify.",
    "auth.googleFailed": "Google sign-in failed",
    "auth.pwRuleLength": "At least 8 characters",
    "auth.pwRuleUppercase": "One uppercase letter",
    "auth.pwRuleNumber": "One number",
    "auth.pwRuleSpecial": "One special character",
    "auth.pwRulesNotMet": "Password does not meet all requirements",

    // ─── COMMON ───
    "back": "Back",
    "business": "Business",
    "city": "City",
    "targetDate": "Target Date",
    "space": "Space",
    "seats": "Seats",
    "seating": "Seating",
    "service": "Service",
    "hours": "Hours",
    "daysWeek": "Days/Week",
    "team": "Team",
    "estTimeline": "Est. Timeline",
    "totalWeeks": "Total Weeks",
    "essential": "essential",
    "recommended": "recommended",
    "optional": "optional",
    "week": "Week",
    "weeks": "weeks",
    "month": "Month",
    "custom": "custom",

    // ─── LEASE TRACKER ───
    "lease.title": "Lease Tracker",
    "lease.enterDate": "Enter your lease expiration date and we'll track the countdown, plus surface negotiation tips when it's time to renew.",
    "lease.expirationDate": "Lease Expiration Date",
    "lease.save": "Save",
    "lease.expires": "Lease expires",
    "lease.overdue": "{days}d overdue",
    "lease.daysLeft": "{days}d",
    "lease.negotiationGuide": "Lease Negotiation Guide — Start Now",
    "lease.goodShape": "You're in good shape. Renewal tips will appear 180 days before your lease expires.",
    "lease.changeDate": "Change Date",
  },
  es: {
    // ─── NAV ───
    "nav.howItWorks": "Cómo funciona",
    "nav.preview": "Vista previa",
    "nav.pricing": "Precios",
    "nav.goToApp": "Ir a la app",
    "nav.getStarted": "Comenzar",
    "nav.signIn": "Iniciar sesión",
    "nav.back": "Volver",
    "nav.dashboard": "Panel",

    // ─── HERO ───
    "hero.title": "Abre tu restaurante, café o bar — con un plan claro.",
    "hero.subtitle": "El kit completo para emprendedores de alimentos y bebidas — desde el primer contrato de alquiler hasta operaciones completas.",
    "hero.cta": "Empieza gratis",
    "hero.cta2": "Mira cómo funciona",
    "hero.trusted": "Usado por emprendedores primerizos y operadores experimentados",
    "hero.stopGuessing": "Abre con inteligencia.",
    "hero.startOpening": "Opera mejor.",
    "hero.createPlan": "Crea tu plan de apertura",
    "hero.seeLiveExample": "Ver ejemplo en vivo",
    "hero.freeToStart": "Gratis para empezar · Sin tarjeta de crédito",
    "hero.saveResearch": "Ahorra 3–6 meses de investigación",
    "hero.badge": "Para restaurantes, hoteles, cafés y conceptos de hostelería",

    // ─── HOW IT WORKS ───
    "how.title": "Cómo funciona",
    "how.subtitle": "Tres pasos. Un plan. Sin experiencia necesaria.",
    "how.step1.title": "Dinos qué vas a abrir",
    "how.step1.desc": "Elige tu ciudad, tipo de negocio, superficie, presupuesto y fecha de apertura.",
    "how.step2.title": "Obtén tu hoja de ruta con IA",
    "how.step2.desc": "Generamos tus permisos, presupuesto, cronograma, sugerencias de ubicación y riesgos al instante.",
    "how.step3.title": "Abre con confianza",
    "how.step3.desc": "Usa tu hoja de ruta, exporta tu plan y pregunta al asistente de IA.",

    // ─── FEATURES ───
    "features.title": "Todo lo que necesitas para abrir",
    "features.subtitle": "Una plataforma reemplaza semanas de investigación, consultores costosos y hojas de cálculo dispersas.",
    "features.location.title": "Encuentra la ubicación correcta",
    "features.location.desc": "Consulta datos reales de arrendamiento, compatibilidad del barrio, expectativas de renta y riesgos de zonificación antes de firmar.",
    "features.permits.title": "Gestiona todos los permisos",
    "features.permits.desc": "Conoce exactamente qué permisos y licencias necesitas, cuánto cuestan, cuánto tardan y dónde solicitarlos.",
    "features.budget.title": "Construye un presupuesto realista",
    "features.budget.desc": "Estima los costos iniciales, el gasto mensual y el punto de equilibrio antes de comprometerte con un espacio.",
    "features.timeline.title": "Sigue un cronograma semana a semana",
    "features.timeline.desc": "Obtén una hoja de ruta clara desde la constitución de la empresa hasta las inspecciones, la contratación y el día de apertura.",
    "features.mistakes.title": "Evita errores costosos",
    "features.mistakes.desc": "Detecta problemas comunes a tiempo, desde retrasos en licencias de licor hasta requisitos de trampa de grasa y conflictos de zonificación.",
    "features.export.title": "Exporta y comparte tu plan",
    "features.export.desc": "Descarga un PDF profesional para usar con socios, propietarios, bancos o inversores.",

    // ─── ROADMAP ───
    "roadmap.title": "Tu Hoja de Ruta con IA",
    "roadmap.subtitle": "Tu plan de apertura completo, generado en segundos.",
    "roadmap.desc": "Responde unas preguntas simples y Oplif™ genera una hoja de ruta personalizada para tu negocio — cubriendo permisos, costos, cronograma, guía de arrendamiento y errores comunes a evitar.",
    "roadmap.bullet1": "Todos los permisos mapeados",
    "roadmap.bullet2": "Presupuesto realista",
    "roadmap.bullet3": "Cronograma semana a semana",
    "roadmap.bullet4": "Recomendaciones de ubicación",
    "roadmap.bullet5": "Alertas de riesgo antes de firmar",
    "roadmap.bullet6": "PDF exportable listo para inversores",

    // ─── FOUNDING MEMBER ───
    "founding.spotsRemaining": "{count} de 50 plazas disponibles",
    "founding.limited": "Limitado a 50 plazas",
    "founding.title": "Sé uno de nuestros primeros 50 miembros fundadores",
    "founding.desc": "Obtén acceso gratuito a Pro a cambio de comentarios honestos. Ayúdanos a construir la mejor plataforma de apertura para fundadores de restaurantes.",
    "founding.joining": "Uniéndose...",
    "founding.join": "Unirse como Miembro Fundador",
    "founding.alreadySignedUp": "¡Ya te has registrado!",
    "founding.error": "Algo salió mal. Inténtalo de nuevo.",
    "founding.success": "¡Estás dentro! Revisa tu correo pronto ✓",

    // ─── PRICING ───
    "pricing.title": "Precios simples y transparentes",
    "pricing.subtitle": "Empieza gratis. Sube de plan cuando estés listo.",
    "pricing.monthly": "Mensual",
    "pricing.annual": "Anual",
    "pricing.save20": "Ahorra 20%",
    "pricing.custom": "Personalizado",
    "pricing.mo": "/mes",
    "pricing.noCreditCard": "Sin tarjeta de crédito",
    "pricing.free.name": "Gratis",
    "pricing.free.micro": "Explora la plataforma. Descubre lo que es posible.",
    "pricing.free.cta": "Empezar gratis",
    "pricing.free.f1": "1 hoja de ruta",
    "pricing.free.f2": "Lista básica de permisos",
    "pricing.free.f3": "Cronograma básico",
    "pricing.free.f4": "5 preguntas de IA",
    "pricing.free.x1": "Sin exportación PDF",
    "pricing.free.x2": "Sin mapa de ubicación",
    "pricing.free.x3": "Sin pronóstico",
    "pricing.free.x4": "Sin modo post-apertura",
    "pricing.pro.name": "Pro",
    "pricing.pro.micro": "Todo lo que necesitas para abrir y operar.",
    "pricing.pro.cta": "Empezar a crecer",
    "pricing.pro.badge": "Más Popular",
    "pricing.pro.f1": "10 hojas de ruta",
    "pricing.pro.f2": "Todo lo de Free, y más",
    "pricing.pro.f3": "Mapa de ubicación",
    "pricing.pro.f4": "Asistente IA ilimitado",
    "pricing.pro.f5": "Operaciones post-apertura",
    "pricing.pro.f6": "Prep. inspección sanitaria",
    "pricing.pro.f7": "Seguimiento de arrendamiento",
    "pricing.pro.f8": "Recordatorios de renovación",
    "pricing.pro.f9": "Puntaje de supervivencia",
    "pricing.pro.f10": "Soporte prioritario",
    "pricing.studio.name": "Studio",
    "pricing.studio.micro": "Para equipos con múltiples conceptos.",
    "pricing.studio.cta": "Escalar",
    "pricing.studio.f1": "25 hojas de ruta",
    "pricing.studio.f2": "Todo en Pro",
    "pricing.studio.f3": "5 miembros del equipo",
    "pricing.studio.f4": "PDF marca blanca",
    "pricing.studio.f5": "Panel de analíticas",
    "pricing.studio.f6": "Gerente de cuenta dedicado",
    "pricing.enterprise.name": "Enterprise",
    "pricing.enterprise.micro": "Para grupos multi-unidad y equipos grandes.",
    "pricing.enterprise.cta": "Contáctanos",
    "pricing.enterprise.f1": "Todo ilimitado",
    "pricing.enterprise.f2": "SSO",
    "pricing.enterprise.f3": "Acceso API",
    "pricing.enterprise.f4": "Integraciones personalizadas",
    "pricing.enterprise.f5": "Onboarding dedicado",

    // ─── FINAL CTA ───
    "cta.title": "Abre más inteligente. Gasta menos. Evita errores costosos.",
    "cta.subtitle": "Obtén tu hoja de ruta de apertura específica para tu ciudad con permisos, presupuesto, cronograma y guía de ubicación en minutos.",
    "cta.button": "Obtener mi plan de apertura",
    "cta.free": "Gratis para empezar · Sin tarjeta de crédito",

    // ─── FOOTER ───
    "footer.tagline": "El kit completo para emprendedores de alimentos y bebidas — desde el primer contrato de alquiler hasta operaciones completas.",
    "footer.product": "Producto",
    "footer.company": "Empresa",
    "footer.legal": "Legal",
    "footer.about": "Acerca de",
    "footer.privacy": "Política de Privacidad",
    "footer.terms": "Términos de Servicio",
    "footer.copyright": "© 2026 Oplif™. Todos los derechos reservados.",

    // ─── PDF ───
    "pdf.download": "Descargar PDF",
    "pdf.preview": "Vista previa del plan",
    "pdf.downloaded": "¡PDF descargado!",
    "pdf.error": "Error al generar el PDF. Inténtalo de nuevo.",

    // ─── DASHBOARD ───
    "dash.title": "Tu {business} en {city}",
    "dash.subtitle": "Aquí está tu hoja de ruta personalizada.",
    "dash.viewPlan": "Ver y Descargar Plan",
    "dash.quickDownload": "Descarga Rápida",
    "dash.openingSummary": "Resumen de Apertura",
    "dash.beginnerMode": "Modo principiante",
    "dash.askAI": "Preguntar IA",
    "dash.timeline": "Cronograma",
    "dash.budget": "Presupuesto",
    "dash.openingScore": "Puntuación de Apertura",
    "dash.budgetFit": "Ajuste de Presupuesto",
    "dash.weekByWeek": "Cronograma Semanal",
    "dash.estimatedStaffing": "Personal Estimado",
    "dash.totalMonthlyLabor": "Costo mensual total",
    "dash.spaceGuidance": "Guía de Espacio",
    "dash.recommendedSqft": "Superficie recomendada",
    "dash.startupCost": "Desglose de Costos Iniciales",
    "dash.total": "Total",
    "dash.milestones": "Hitos Antes de la Apertura",
    "dash.openingDay": "Día de apertura",
    "dash.before": "antes",
    "dash.commonMistakes": "Errores Comunes a Evitar",
    "dash.first7Days": "Tus Primeros 7 Días",
    "dash.locations": "Recomendaciones de Ubicación",
    "dash.openingTimeline": "Cronograma de Apertura",
    "dash.permits": "Permisos y Licencias",
    "dash.budgetPlanner": "Planificador de Presupuesto",
    "dash.forecast": "Pronóstico Financiero",
    "dash.checklist": "Lista de Verificación",
    "dash.assistant": "Asistente IA",
    "dash.shareRoadmap": "Compartir Hoja de Ruta",
    "dash.shareCopied": "Enlace copiado — envíalo a tu socio o inversor.",
    "dash.tabOpening": "Plan de Apertura",
    "dash.tabOperations": "Operaciones",
    "dash.tabLocations": "Ubicaciones",
    "dash.readinessScore": "Tu Puntuación de Preparación",
    "dash.readinessTooltip": "Esta puntuación estima qué tan preparado estás para abrir según tu presupuesto, ubicación y completitud del plan. 80+ es excelente, menos de 60 significa que puedes tener vacíos que abordar.",
    "dash.outOf100": "de 100",
    "dash.greatShape": "¡Estás en excelente forma!",
    "dash.fewThings": "Algunas cosas por mejorar",
    "dash.needsPrep": "Necesita más preparación",
    "dash.strongPosition": "Posición Fuerte",
    "dash.needsAttention": "Necesita Atención",
    "dash.highRisk": "Alto Riesgo",
    "dash.startupInvestment": "Inversión Inicial",
    "dash.startupTooltip": "Este es el costo total estimado para abrir tu negocio — incluyendo construcción, equipos, permisos e inventario inicial.",
    "dash.howMuchStart": "Cuánto necesitarás para empezar",
    "dash.yourBudget": "Tu presupuesto: {label}",
    "dash.budgetLabel": "Presupuesto: {label} · Ajuste: ",
    "dash.monthlyLabor": "Costo Laboral Mensual",
    "dash.monthlyStaffCosts": "Costos mensuales de personal",
    "dash.laborTooltip": "Esto es lo que gastarás cada mes en salarios de empleados. Suele ser tu mayor costo continuo — intenta mantenerlo por debajo del 30% de los ingresos.",
    "dash.essentialRoles": "{count} roles indispensables",
    "dash.essentialRolesDetail": "{count} roles esenciales · {team}",
    "dash.timeToOpen": "Tiempo para abrir",
    "dash.permitsNeeded": "Permisos necesarios",
    "dash.rolesToHire": "Roles a contratar",
    "dash.todoItems": "Tareas pendientes",
    "dash.requiredPermits": "{count} Permisos Requeridos",
    "dash.checklistItems": "{count} elementos de lista",
    "dash.riskFlags": "{count} Alertas de Riesgo",
    "dash.founderMistakes": "Errores comunes de fundadores a evitar",
    "dash.setupDetails": "Detalles de tu Configuración",
    "dash.setupTooltip": "Estos son los detalles que ingresaste durante el registro. Impulsan todas las estimaciones y recomendaciones en esta página.",
    "dash.whoToHire": "A quién necesitarás contratar",
    "dash.staffingTooltip": "Estos son los roles que probablemente necesitarás. Los roles 'Esenciales' son imprescindibles desde el primer día. Los 'Recomendados' ayudan a medida que creces.",
    "dash.optionalHidden": "+ {count} roles opcionales ocultos · desactiva el Modo Principiante para ver todos",
    "dash.totalWages": "Total de salarios mensuales",
    "dash.howMuchSpace": "Cuánto espacio necesitas",
    "dash.spaceTooltip": "Según tu tipo de negocio y necesidades de asientos, este es el rango de superficie que funciona mejor. Muy pequeño y estarás apretado; muy grande y la renta se come tus ganancias.",
    "dash.recommendedSize": "Tamaño recomendado",
    "dash.seatingCapacity": "Capacidad de Asientos",
    "dash.indoor": "Interior",
    "dash.outdoor": "Exterior",
    "dash.keyDeadlines": "Fechas clave",
    "dash.milestonesTooltip": "Estas son las fechas límite importantes que debes cumplir antes del día de apertura. Las que tienen un punto verde son las más críticas — no las pierdas.",
    "dash.budgetReality": "Verificación de Presupuesto",
    "dash.budgetTight": "Según datos del mercado, tu presupuesto de {label} puede ser ajustado — el mínimo realista es {min}. Esto es lo que los fundadores suelen subestimar:",
    "dash.permitFees": "Tarifas de permisos y licencias",
    "dash.preOpenPayroll": "Nómina previa a la apertura",
    "dash.contingencyBuffer": "Reserva de contingencia (18%)",
    "dash.lockedTimeline": "Cronograma Completo — Plan Pro",
    "dash.lockedTimelineDesc": "Mejora tu plan para ver cada fase de tu apertura mapeada semana a semana.",
    "dash.lockedStaffing": "Análisis de Personal y Espacio — Plan Pro",
    "dash.lockedStaffingDesc": "Obtén recomendaciones de contratación por rol y guía de distribución del espacio adaptada a tu concepto.",
    "dash.lockedMilestones": "Hitos de Apertura — Plan Pro",
    "dash.lockedMilestonesDesc": "Ve cada hito crítico mapeado a tu fecha de apertura para que nada se escape.",
    "dash.days": "días",

    // ─── LOCATIONS ───
    "loc.title": "Recomendaciones de Ubicación",
    "loc.available": "Disponible para alquilar",
    "loc.leased": "Alquilado",
    "loc.searchLeases": "Buscar arriendos disponibles en",
    "loc.linksOpen": "Los enlaces abren listados comerciales filtrados para",
    "loc.neighborhoods": "Análisis de Vecindarios",
    "loc.rent": "Renta",
    "loc.traffic": "Tráfico",
    "loc.beginnerOK": "Para principiantes",
    "loc.fitScore": "Puntuación",
    "loc.beginnerTip": "Esta zona es recomendada para emprendedores primerizos por su renta accesible y comunidad local.",
    "loc.neighborhoodGuide": "Guía de Vecindarios de {city}",
    "loc.bestAreas": "Mejores zonas para tu {business} — ordenadas por compatibilidad",
    "loc.neighborhoodAnalysis": "Análisis de vecindarios para {city}",
    "loc.liveListings": "📍 Listados en vivo próximamente — usa esta guía para planificar tu estrategia de ubicación en {city}.",
    "loc.cityNote": "⚠️ Nota de {city}",
    "loc.bestMatch": "Mejor Coincidencia",
    "loc.lowestRent": "Menor Renta",
    "loc.highestTraffic": "Mayor Tráfico",
    "loc.upAndComing": "En Ascenso",
    "loc.map": "Mapa",
    "loc.cards": "Tarjetas",
    "loc.matchingFor": "Coincidencia para:",
    "loc.greatMatch": "Gran coincidencia (85%+)",
    "loc.goodMatch": "Buena coincidencia (70–84%)",
    "loc.possible": "Posible (<70%)",
    "loc.avgSpend": "Gasto Prom.",
    "loc.competition": "Competencia",
    "loc.insiderTip": "Consejo interno:",
    "loc.comingSoon": "Análisis de vecindarios próximamente para {city}",
    "loc.comingSoonDesc": "Estamos investigando vecindarios, rentas, tráfico peatonal y datos de competencia para {city}. Obtendrás puntuaciones de compatibilidad adaptadas a tu concepto de {business}.",
    "loc.notifyMe": "Notificarme cuando esté disponible",
    "loc.backToDashboard": "Volver al Panel",
    "loc.inTheMeantime": "💡 Mientras tanto, usa el {link} para preguntar sobre vecindarios en {city} — conoce bien la ciudad.",
    "loc.aiAssistant": "Asistente IA",

    // ─── TIMELINE ───
    "timeline.title": "Cronograma de Apertura",
    "timeline.estimated": "Estimado: {time}",
    "timeline.liquorWarning": "Importante — Presenta tu solicitud de licencia de licor en la Semana 1",
    "timeline.liquorDetail": "No esperes hasta que tu espacio esté listo. El proceso de licencia de licor de la SLA de Florida toma un mínimo de 3–6 meses y es la razón #1 por la que los restaurantes abren tarde en Miami.",
    "timeline.taskCalendar": "Calendario de Tareas",
    "timeline.assign": "Asignar",
    "timeline.notes": "Notas",
    "timeline.addNote": "Agregar nota",
    "timeline.save": "Guardar",
    "timeline.cancel": "Cancelar",
    "timeline.addNotePlaceholder": "Agregar una nota…",

    // ─── PERMITS ───
    "permits.title": "Permisos y Licencias",
    "permits.verified": "{count} permisos verificados para tu ciudad",
    "permits.whatYouNeed": "Lo que podrías necesitar — con detalles",
    "permits.realData": "Datos gubernamentales reales",
    "permits.costsVerified": "Costos verificados de fuentes oficiales .gov · Última actualización: {date}",
    "permits.required": "Requeridos",
    "permits.optional": "Opcionales",
    "permits.total": "Total",
    "permits.searchPermits": "Buscar permisos...",
    "permits.expandAll": "Expandir Todo",
    "permits.collapse": "Colapsar",
    "permits.all": "Todos",
    "permits.processingTime": "Tiempo de Procesamiento",
    "permits.estimatedCost": "Costo Estimado",
    "permits.whatYouNeedDocs": "Lo Que Necesitas",
    "permits.applyNow": "Solicitar Ahora — Sitio Oficial",
    "permits.brokenLink": "Reportar enlace roto",
    "permits.brokenLinkReported": "Enlace roto reportado",
    "permits.brokenLinkDesc": "Verificaremos el enlace de \"{name}\" y lo actualizaremos.",
    "permits.ifBroken": "Si este enlace no funciona, busca \"{name} {city}\" en Google.",
    "permits.govWarning": "Los sitios web gubernamentales cambian URLs ocasionalmente. Si este enlace no funciona, busca \"{name} {city}\" en Google.",
    "permits.infoRequired": "Estos son los permisos exactos requeridos en {city}. Toca cualquier permiso para ver costos, tiempos y enlaces de solicitud directos.",
    "permits.infoGeneric": "Los requisitos dependen de la ciudad, condado, estado y modelo de negocio. Siempre verifica con las autoridades locales.",
    "permits.lockedCount": "{count} permisos más bloqueados",
    "permits.upgradeToSee": "Mejora a Opener para ver todos los permisos",
    "permits.upgrade": "Mejorar",
    "permits.mayBeNeeded": "Podría Ser Necesario ({count})",
    "permits.noMatch": "Ningún permiso coincide con tu búsqueda.",

    // ─── BUDGET ───
    "budget.title": "Planificador de Presupuesto",
    "budget.itemized": "Costos iniciales detallados para un {business}",
    "budget.advisory": "Aviso de Presupuesto",
    "budget.miamiWarning": "Atención — abrir un restaurante en Miami típicamente requiere al menos {amount} en capital inicial. Considera ajustar tu presupuesto antes de continuar.",
    "budget.strong": "Presupuesto Fuerte",
    "budget.balanced": "Presupuesto Equilibrado",
    "budget.underfunded": "Presupuesto Insuficiente",
    "budget.strongMsg": "Tu presupuesto se ve sólido para este tipo de negocio.",
    "budget.balancedMsg": "Tu presupuesto es viable pero deja margen para sorpresas.",
    "budget.underfundedMsg": "Tu presupuesto puede ser ajustado. Considera un formato más pequeño o una zona de menor costo.",
    "budget.estimatedTotal": "Rango Total Estimado",
    "budget.basedOn": "Basado en costos típicos para un {business} en {city}. Ajustado por renta local, mano de obra y costos de construcción.",
    "budget.disclaimer": "⚠️ Aviso: Estas son estimaciones ajustadas al mercado de {city} usando índices de renta local, mano de obra y costos de construcción. Los costos reales varían según la ubicación exacta, condición del espacio, calidad del equipo (nuevo vs usado) y proveedor. Siempre obtén 3+ cotizaciones antes de comprometerte.",
    "budget.alcoholGuidance": "Guía de Licencia de Alcohol",
    "budget.compareOptions": "Compara tus opciones:",
    "budget.licenseType": "Tipo de Licencia",
    "budget.cost": "Costo",
    "budget.timelineLabel": "Plazo",
    "budget.details": "Detalles",

    // ─── CHECKLIST ───
    "checklist.title": "Plan de Apertura",
    "checklist.stepsComplete": "{done} de {total} pasos completados",
    "checklist.complete": "completado",
    "checklist.foundation": "Base",
    "checklist.halfway": "Mitad",
    "checklist.almostThere": "Casi Listo",
    "checklist.readyToOpen": "¡Listo para Abrir!",
    "checklist.done": "Hecho",
    "checklist.remaining": "Pendiente",
    "checklist.daysToOpening": "~{days} días para la apertura",
    "checklist.readyEmoji": "🎉 ¡Listo para abrir!",
    "checklist.whereToGet": "Dónde obtenerlo:",

    // ─── ASSISTANT ───
    "assistant.title": "Asistente de Negocios IA",
    "assistant.helping": "Ayudándote a abrir tu {business}",
    "assistant.askAnything": "Pregunta lo que sea sobre abrir tu negocio",
    "assistant.queries": "{used} de {limit} consultas",
    "assistant.placeholder": "Pregunta sobre permisos, ubicaciones, presupuesto, personal...",
    "assistant.signIn": "Inicia sesión para usar el asistente.",
    "assistant.noResponse": "No se recibió respuesta.",
    "assistant.connectionError": "Error de conexión. Verifica tu internet e inténtalo de nuevo.",
    "assistant.limitReached": "Has usado todas tus {limit} consultas IA este mes.",
    "assistant.upgradeMore": "Mejorar para más →",

    // ─── FORECAST ───
    "forecast.title": "Pronóstico Financiero",
    "forecast.subtitle": "Estima tus ingresos, costos y punto de equilibrio — antes de gastar un dólar.",
    "forecast.disclaimer": "Esta es una herramienta de planificación estimada, no asesoramiento financiero garantizado. Los resultados reales variarán según la ubicación, ejecución y condiciones del mercado.",
    "forecast.assumptions": "Tus Supuestos",
    "forecast.avgTicket": "Ticket Promedio ({cs})",
    "forecast.customersPerDay": "Clientes por Día",
    "forecast.monthlyRent": "Renta Mensual ({cs})",
    "forecast.cogs": "COGS (%)",
    "forecast.laborPercent": "Costo Laboral (%)",
    "forecast.otherExpenses": "Otros Gastos Mensuales ({cs})",
    "forecast.startupBudget": "Presupuesto inicial:",
    "forecast.daysWeek": "{days} días/semana",
    "forecast.conservative": "Conservador",
    "forecast.realistic": "Realista",
    "forecast.optimistic": "Optimista",
    "forecast.monthlyRevenue": "Ingresos Mensuales",
    "forecast.monthlyProfit": "Ganancia Mensual",
    "forecast.cashFlowPositive": "Flujo de Caja Positivo",
    "forecast.fullROI": "Plazo de Retorno Total",
    "forecast.atFullCapacity": "a plena capacidad",
    "forecast.profitable": "rentable",
    "forecast.operatingLoss": "operando con pérdidas",
    "forecast.revenueCovers": "los ingresos cubren gastos mensuales",
    "forecast.revenueVsExpenses": "Ingresos vs Gastos (12 meses)",
    "forecast.profitComparison": "Comparación de Ganancias — Todos los Escenarios",
    "forecast.monthlyBreakdown": "Desglose Mensual — {scenario}",
    "forecast.month": "Mes",
    "forecast.revenue": "Ingresos",
    "forecast.expenses": "Gastos",
    "forecast.profit": "Ganancia",
    "forecast.cumCash": "Efectivo Acum.",
    "forecast.labor": "Laboral",
    "forecast.other": "Otros",
    "forecast.scenarioSummary": "Resumen de Escenarios",
    "forecast.annualRevenue": "Ingresos anuales",
    "forecast.annualProfit": "Ganancia anual",
    "forecast.fullRoi": "Retorno total",
    "forecast.assumptionsMethod": "Supuestos y Metodología",

    // ─── EQUIPMENT ───
    "equipment.title": "Equipos e Inventario",
    "equipment.subtitle": "Todo lo que necesitas antes del día de apertura",
    "equipment.progress": "Progreso",
    "equipment.acquired": "{count} de {total} adquiridos",
    "equipment.acquiredCost": "Adquirido: ~{amount}",
    "equipment.estTotal": "Total Est.: ~{amount}",
    "equipment.allCategories": "Todas las Categorías",
    "equipment.allPriorities": "Todas las Prioridades",
    "equipment.addItem": "Agregar Artículo",
    "equipment.addCustomItem": "Agregar Artículo Personalizado",
    "equipment.emoji": "Emoji",
    "equipment.name": "Nombre *",
    "equipment.minPrice": "Precio Mín. ({cs})",
    "equipment.maxPrice": "Precio Máx. ({cs})",
    "equipment.category": "Categoría",
    "equipment.priority": "Prioridad",
    "equipment.proTip": "Consejo",
    "equipment.whereToBuy": "Dónde Comprar",
    "equipment.itemRequired": "El nombre del artículo es requerido",
    "equipment.itemAdded": "¡Artículo agregado!",

    // ─── ONBOARDING ───
    "onboarding.step": "Paso {current} de {total}",
    "onboarding.welcome": "Bienvenido a oplif.™",
    "onboarding.welcomeDesc": "La manera más inteligente de planificar, abrir y administrar tu negocio de alimentos y bebidas.",
    "onboarding.personalize": "Personalizaremos tu experiencia en menos de 60 segundos.",
    "onboarding.tellUs": "Cuéntanos sobre tu negocio",
    "onboarding.tellUsDesc": "Esto nos ayuda a personalizar tu hoja de ruta, permisos y estimaciones de costos para tu mercado específico.",
    "onboarding.businessName": "Nombre del negocio",
    "onboarding.businessNamePlaceholder": "ej. El Plato Dorado",
    "onboarding.businessType": "Tipo de negocio",
    "onboarding.country": "País",
    "onboarding.selectCountry": "Selecciona un país",
    "onboarding.city": "Ciudad",
    "onboarding.goal": "¿Cuál es tu objetivo?",
    "onboarding.goalDesc": "Personalizaremos tu panel según dónde estés en tu camino.",
    "onboarding.goalOpening": "Abrir un nuevo negocio",
    "onboarding.goalOpeningDesc": "Estoy planeando lanzar y necesito una hoja de ruta paso a paso para llegar ahí.",
    "onboarding.goalManaging": "Administrar uno existente",
    "onboarding.goalManagingDesc": "Opera con más precisión: seguimiento en tiempo real de costos de personal, alimentos y alquiler, alertas automáticas de renovación de permisos y contratos, puntuación mensual de supervivencia y comparativas con locales similares de tu mercado.",
    "onboarding.choosePlan": "Elige tu plan",
    "onboarding.choosePlanDesc": "Empieza gratis, mejora cuando estés listo.",
    "onboarding.letsGo": "Vamos",
    "onboarding.continue": "Continuar",
    "onboarding.launchDashboard": "Lanzar mi panel",
    "onboarding.popular": "Popular",
    "onboarding.comingSoon": "Próximamente",

    // ─── AUTH ───
    "auth.welcomeBack": "Bienvenido de vuelta",
    "auth.createAccount": "Crea tu cuenta",
    "auth.signInDesc": "Inicia sesión para continuar donde lo dejaste.",
    "auth.signUpDesc": "Únete a miles de fundadores que planifican aperturas más inteligentes.",
    "auth.email": "Correo electrónico",
    "auth.password": "Contraseña",
    "auth.signIn": "Iniciar sesión",
    "auth.signUp": "Crear cuenta",
    "auth.or": "o",
    "auth.continueGoogle": "Continuar con Google",
    "auth.noAccount": "¿No tienes cuenta?",
    "auth.hasAccount": "¿Ya tienes cuenta?",
    "auth.signUpLink": "Registrarse",
    "auth.signInLink": "Iniciar sesión",
    "auth.accountCreated": "Cuenta creada — revisa tu correo para verificar.",
    "auth.googleFailed": "Error al iniciar sesión con Google",
    "auth.pwRuleLength": "Al menos 8 caracteres",
    "auth.pwRuleUppercase": "Una letra mayúscula",
    "auth.pwRuleNumber": "Un número",
    "auth.pwRuleSpecial": "Un carácter especial",
    "auth.pwRulesNotMet": "La contraseña no cumple todos los requisitos",

    // ─── COMMON ───
    "back": "Volver",
    "business": "Negocio",
    "city": "Ciudad",
    "targetDate": "Fecha Objetivo",
    "space": "Espacio",
    "seats": "Asientos",
    "seating": "Tipo",
    "service": "Servicio",
    "hours": "Horario",
    "daysWeek": "Días/Semana",
    "team": "Equipo",
    "estTimeline": "Cronograma Est.",
    "totalWeeks": "Semanas Totales",
    "essential": "esencial",
    "recommended": "recomendado",
    "optional": "opcional",
    "week": "Semana",
    "weeks": "semanas",
    "month": "Mes",
    "custom": "personalizado",

    // ─── LEASE TRACKER ───
    "lease.title": "Rastreador de Arrendamiento",
    "lease.enterDate": "Ingresa tu fecha de vencimiento del arrendamiento y rastrearemos la cuenta regresiva, además de mostrar consejos de negociación cuando sea hora de renovar.",
    "lease.expirationDate": "Fecha de Vencimiento del Arrendamiento",
    "lease.save": "Guardar",
    "lease.expires": "El arrendamiento vence",
    "lease.overdue": "{days}d vencido",
    "lease.daysLeft": "{days}d",
    "lease.negotiationGuide": "Guía de Negociación de Arrendamiento — Empieza Ahora",
    "lease.goodShape": "Estás en buena forma. Los consejos de renovación aparecerán 180 días antes de que venza tu arrendamiento.",
    "lease.changeDate": "Cambiar Fecha",
  },
};

export default translations;
